import React, { createContext, forwardRef, useContext, useMemo } from 'react';
import { Alert, AlertTitle, Box, Chip } from '@mui/material';
import { SnackbarProvider, SnackbarContent, useSnackbar } from 'notistack';

export const NotificacionContext = createContext({
  mostrarNotificacion: () => {},
  cerrarNotificacion: () => {},
});

const SEVERIDAD = { success: 'success', error: 'error', warning: 'warning', info: 'info', default: 'info' };

// Cada aviso se dibuja con el Alert del tema, igual que los avisos locales de las vistas.
// `destacado` lo agranda para confirmaciones que no se deben pasar por alto.
const Aviso = forwardRef(({ id, message, variant, destacado, titulo, idRecurso }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const severidad = SEVERIDAD[variant] || 'info';
  const conId = idRecurso !== undefined && idRecurso !== null && idRecurso !== '';

  return (
    <SnackbarContent ref={ref} role="alert">
      <Alert
        severity={severidad}
        onClose={() => closeSnackbar(id)}
        sx={{
          width: '100%',
          alignItems: 'center',
          boxShadow: (t) => t.shadows[destacado ? 10 : 6],
          minWidth: { sm: destacado ? 420 : 340 },
          maxWidth: destacado ? 600 : 480,
          ...(destacado && {
            p: 2,
            fontSize: '1.05rem',
            '& .MuiAlert-icon': { fontSize: 32, mr: 1.5 },
          }),
        }}
      >
        {titulo && (
          <AlertTitle sx={{ fontSize: destacado ? '1.2rem' : '1rem', fontWeight: 700 }}>
            {titulo}
          </AlertTitle>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {conId && (
            <Chip
              label={`ID ${idRecurso}`}
              color={severidad}
              size={destacado ? 'medium' : 'small'}
              sx={{ fontWeight: 700, fontSize: destacado ? '0.95rem' : undefined }}
            />
          )}
          <Box component="span">{message}</Box>
        </Box>
      </Alert>
    </SnackbarContent>
  );
});

const COMPONENTES = { default: Aviso, success: Aviso, error: Aviso, warning: Aviso, info: Aviso };

const Puente = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // mostrarNotificacion(mensaje, tipo, { destacado, titulo, idRecurso })
  const value = useMemo(
    () => ({
      mostrarNotificacion: (mensaje, tipo = 'success', opciones = {}) => {
        const { destacado = false, titulo, idRecurso } = opciones;
        let duracion = 5000;
        if (destacado) duracion = 10000;
        else if (tipo === 'error') duracion = 9000;

        return enqueueSnackbar(mensaje, {
          variant: SEVERIDAD[tipo] ? tipo : 'info',
          autoHideDuration: duracion,
          destacado,
          titulo,
          idRecurso,
          // Dos normativas seguidas con el mismo nombre deben confirmarse las dos veces
          ...(destacado && { preventDuplicate: false }),
        });
      },
      cerrarNotificacion: () => closeSnackbar(),
    }),
    [enqueueSnackbar, closeSnackbar]
  );

  return <NotificacionContext.Provider value={value}>{children}</NotificacionContext.Provider>;
};

export const NotificacionProvider = ({ children }) => (
  <SnackbarProvider
    maxSnack={3}
    preventDuplicate
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    classes={{ containerAnchorOriginTopRight: 'an-toast-top' }}
    Components={COMPONENTES}
  >
    <Puente>{children}</Puente>
  </SnackbarProvider>
);

export const useNotificacion = () => useContext(NotificacionContext);
